import { createI18n } from 'vue-i18n'
import es from './locales/es.json'
import en from './locales/en.json'

export const SUPPORTED_LOCALES = Object.freeze([
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
])

const STORAGE_KEY = 'iris.locale'
const FALLBACK = 'es'

function detectLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && SUPPORTED_LOCALES.some((item) => item.code === stored)) return stored
  } catch {
    /* almacenamiento no disponible */
  }
  const fromEnv = import.meta.env?.VITE_DEFAULT_LOCALE
  if (fromEnv && SUPPORTED_LOCALES.some((item) => item.code === fromEnv)) return fromEnv

  const fromBrowser = navigator?.language?.slice(0, 2)
  return SUPPORTED_LOCALES.some((item) => item.code === fromBrowser) ? fromBrowser : FALLBACK
}

/**
 * Instancia global. Se usa el modo Composition API de vue-i18n (`legacy: false`)
 * para poder llamar a `i18n.global.t` fuera de los componentes — la capa de
 * servicios traduce los errores sin conocer nada de Vue.
 */
export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: detectLocale(),
  fallbackLocale: FALLBACK,
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
  messages: { es, en },
  // Todo el dinero del sistema es USD con cuatro decimales (contrato de la API);
  // el formateo con menos decimales se hace en la capa de presentación.
  numberFormats: {
    es: {
      currency: { style: 'currency', currency: 'USD', minimumFractionDigits: 2 },
      currencyPrecise: { style: 'currency', currency: 'USD', minimumFractionDigits: 4 },
      decimal: { style: 'decimal', maximumFractionDigits: 1 },
      integer: { style: 'decimal', maximumFractionDigits: 0 },
      percent: { style: 'percent', maximumFractionDigits: 1 },
    },
    en: {
      currency: { style: 'currency', currency: 'USD', minimumFractionDigits: 2 },
      currencyPrecise: { style: 'currency', currency: 'USD', minimumFractionDigits: 4 },
      decimal: { style: 'decimal', maximumFractionDigits: 1 },
      integer: { style: 'decimal', maximumFractionDigits: 0 },
      percent: { style: 'percent', maximumFractionDigits: 1 },
    },
  },
  datetimeFormats: {
    es: {
      short: { day: '2-digit', month: 'short' },
      long: { day: '2-digit', month: 'short', year: 'numeric' },
      time: { hour: '2-digit', minute: '2-digit' },
      full: { dateStyle: 'medium', timeStyle: 'short' },
    },
    en: {
      short: { day: '2-digit', month: 'short' },
      long: { day: '2-digit', month: 'short', year: 'numeric' },
      time: { hour: '2-digit', minute: '2-digit' },
      full: { dateStyle: 'medium', timeStyle: 'short' },
    },
  },
})

export function currentLocale() {
  return i18n.global.locale.value
}

/** Cambia el idioma y lo recuerda. También actualiza `<html lang>` (accesibilidad). */
export function setLocale(locale) {
  if (!SUPPORTED_LOCALES.some((item) => item.code === locale)) return
  i18n.global.locale.value = locale
  document.documentElement.setAttribute('lang', locale)
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    /* almacenamiento no disponible */
  }
}
